import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Archive, Eye, Loader2, RotateCcw, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fetchAgents } from '@/lib/api';
import {
  fetchAgentDeletionEligibility,
  getBlockingAgentDependencies,
  permanentlyDeleteAgent,
  setAgentArchived,
  type AgentDeletionEligibility,
} from '@/lib/api/agentLifecycle';

interface ArchivedAgent {
  readonly id: string;
  readonly name: string;
  readonly title?: string | null;
  readonly type: string;
  readonly status: string;
  readonly completions?: number | null;
  readonly avg_score?: number | null;
  readonly updated_at?: string | null;
}

interface ArchivedAgentsPanelProps {
  readonly tenantId: string;
  readonly onLifecycleChanged: () => void;
}

type EligibilityState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: AgentDeletionEligibility }
  | { status: 'error'; message: string };

function formatDate(value?: string | null): string {
  if (!value) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

function PermanentDeleteDialog({
  agent,
  open,
  onClose,
  onDeleted,
}: {
  readonly agent: ArchivedAgent | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDeleted: () => void;
}) {
  const [eligibility, setEligibility] = useState<EligibilityState>({ status: 'idle' });
  const [confirmationName, setConfirmationName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open || !agent) {
      setEligibility({ status: 'idle' });
      setConfirmationName('');
      setIsDeleting(false);
      return;
    }

    let cancelled = false;
    setEligibility({ status: 'loading' });
    fetchAgentDeletionEligibility(agent.id)
      .then((data) => {
        if (!cancelled) setEligibility({ status: 'success', data });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Unable to check this agent.';
        if (!cancelled) setEligibility({ status: 'error', message });
      });

    return () => {
      cancelled = true;
    };
  }, [agent, open]);

  const handleDelete = async () => {
    if (!agent || eligibility.status !== 'success' || !eligibility.data.canPermanentlyDelete) return;
    if (confirmationName !== agent.name) return;

    setIsDeleting(true);
    try {
      await permanentlyDeleteAgent(agent.id, confirmationName);
      toast.success('Agent permanently deleted.');
      onDeleted();
      onClose();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Unable to permanently delete this agent.');
      setIsDeleting(false);
    }
  };

  const blockers = eligibility.status === 'success'
    ? getBlockingAgentDependencies(eligibility.data)
    : [];
  const canDelete = eligibility.status === 'success'
    && eligibility.data.canPermanentlyDelete
    && confirmationName === agent?.name
    && !isDeleting;

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="h-6 w-6 text-destructive" />
            </div>
            <AlertDialogTitle className="text-2xl">Delete Agent Permanently?</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            Permanent deletion cannot be undone. The server will refuse to delete any agent that has
            training history, user access, organisation access, pathway use or network qualification records.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {eligibility.status === 'loading' && (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking every agent reference…
          </div>
        )}

        {eligibility.status === 'error' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {eligibility.message}
          </div>
        )}

        {eligibility.status === 'success' && !eligibility.data.canPermanentlyDelete && (
          <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
            <div className="flex items-center gap-2 font-medium">
              <ShieldAlert className="h-4 w-4" />
              This agent must remain archived.
            </div>
            {blockers.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {blockers.map((blocker) => (
                  <li key={blocker.key}>• {blocker.count} {blocker.label}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm">Only inactive, non-system agents can be permanently deleted.</p>
            )}
            <p className="text-sm">Restore it for reuse, or leave it archived to preserve its records.</p>
          </div>
        )}

        {eligibility.status === 'success' && eligibility.data.canPermanentlyDelete && agent && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This agent has no protected references. Type its exact name to confirm:
            </p>
            <p className="rounded bg-muted px-3 py-2 text-sm font-medium">{agent.name}</p>
            <Input
              value={confirmationName}
              onChange={(event) => setConfirmationName(event.target.value)}
              placeholder="Type the exact agent name"
              autoComplete="off"
              aria-label="Confirm agent name"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={!canDelete} onClick={handleDelete}>
            {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Delete Permanently
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ArchivedAgentsPanel({ tenantId, onLifecycleChanged }: ArchivedAgentsPanelProps) {
  const navigate = useNavigate();
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ArchivedAgent | null>(null);
  const archivedQuery = useQuery({
    queryKey: ['archived-agents', tenantId],
    queryFn: async () => (
      await fetchAgents({ tenant_id: tenantId, status: 'inactive' })
    ) as ArchivedAgent[],
  });

  const refresh = async () => {
    await archivedQuery.refetch();
    onLifecycleChanged();
  };

  const handleRestore = async (agent: ArchivedAgent) => {
    setRestoringId(agent.id);
    try {
      await setAgentArchived(agent.id, false);
      toast.success(`${agent.name} restored to active agents.`);
      await refresh();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Unable to restore this agent.');
    } finally {
      setRestoringId(null);
    }
  };

  if (archivedQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (archivedQuery.isError) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-destructive">
        Unable to load archived agents. Refresh the page and try again.
      </div>
    );
  }

  const agents = archivedQuery.data || [];
  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-start gap-3">
          <Archive className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="font-semibold">Archived Agents</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Archived agents are hidden from active simulation lists. Restore them at any time. Permanent deletion
              is available only when the server confirms that no training or organisation records depend on the agent.
            </p>
          </div>
        </div>
      </div>

      {agents.length === 0 ? (
        <div className="rounded-xl border bg-card py-14 text-center">
          <Archive className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">No archived agents</p>
          <p className="mt-1 text-sm text-muted-foreground">Agents you archive will appear here.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Historical completions</TableHead>
                <TableHead>Archived</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <p className="font-medium">{agent.name}</p>
                    {agent.title && <p className="text-xs text-muted-foreground">{agent.title}</p>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{agent.type}</Badge></TableCell>
                  <TableCell>
                    <p className="font-semibold">{agent.completions ?? 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {typeof agent.avg_score === 'number' ? `${Math.round(agent.avg_score)}% average` : 'No scored average'}
                    </p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(agent.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/agents/${agent.id}/view`)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View history
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(agent)}
                        disabled={restoringId !== null}
                      >
                        {restoringId === agent.id
                          ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          : <RotateCcw className="mr-2 h-4 w-4" />}
                        Restore
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => setDeleteTarget(agent)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Permanently
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <PermanentDeleteDialog
        agent={deleteTarget}
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onDeleted={refresh}
      />
    </div>
  );
}
